import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import "./DeletedAccount.css";
import axios from "axios";

const DELETE_REASONS = [
  { value: "no_longer_needed", label: "I no longer need this account" },
  { value: "another_account", label: "I have another account" },
  { value: "privacy_concern", label: "Privacy concern" },
  { value: "other", label: "Other" },
];

const DeletedAccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [successfulDelete, setSuccessfulDelete] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
  if (showWarningModal || successfulDelete || showFailureModal) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }
}, [showWarningModal, successfulDelete, showFailureModal]);


  const finalReason = useMemo(() => {
    if (!reason) return "";
    if (reason === "other") return otherReason.trim();
    return reason;
  }, [reason, otherReason]);

  const isDeleteDisabled =
    !reason || loading || (reason === "other" && !otherReason.trim());

  const handleBack = () => navigate("/account");

  const handleDeleteClick = () => {
    if (isDeleteDisabled) return;
    setShowWarningModal(true);
  };

  const handleDelete = async () => {
    if (!finalReason) return;

    setLoading(true);

    try {
      await axios.post("/user/request-delete-account", {
        userId: user._id,
        reason: finalReason,
      });

      setSuccessfulDelete(true);
      setShowWarningModal(false);
    } catch (error) {
      console.error("Account deletion failed:", error);
      setShowFailureModal(true);
      setShowWarningModal(false);
    } finally {
      setLoading(false);
    }
  };

  const finalizeDelete = async () => {
    try {
      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error finalizing account deletion:", error);
    }
  };

  const handleTryAgain = () => {
    setShowFailureModal(false);
    handleDelete();
  };

  return (
    <div className="app-layout">
      <div className="main-content">
        <div className="deleted-page">
          <div className="deleted-header">
            <button className="delete-back-button" onClick={handleBack}>
              ← Back
            </button>
            <h1>Delete Account</h1>
          </div>

          <div className="deleted-content">
            <h2>Delete Account?</h2>
            <p className="deleted-description">
              After this process is activated, your account and all associated
              events and information will be permanently inaccessible. To use
              the same email again, you’ll need to create a new account and
              resubmit KYC details.
            </p>

            <div className="delete-reason-section">
              <h3>Select a reason</h3>

              <div className="delete-reason-options">
                {DELETE_REASONS.map((r) => (
                  <label key={r.value} className="delete-reason-option">
                    <input
                      type="radio"
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>

              {reason === "other" && (
                <div className="delete-other-reason">
                  <label>Other Information</label>
                  <textarea
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Anything else you'd like to tell us..."
                    rows={4}
                  />
                </div>
              )}
            </div>

            <div className="deleted-actions">
              <button className="delete-back-button-secondary" onClick={handleBack}>
                Back
              </button>

              <button
                className="deleted-button"
                onClick={handleDeleteClick}
                disabled={isDeleteDisabled}
              >
                {loading ? "Deleting Account..." : "Delete my Account"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showWarningModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-content">
            {!loading && (
              <button
                className="delete-modal-close"
                onClick={() => setShowWarningModal(false)}
              >
                ×
              </button>
            )}

            <h2>Confirm Account Deletion</h2>

            <p>
              Are you sure you want to permanently delete your account? This
              action cannot be undone.
            </p>

            {loading && (
              <p className="delete-deleting-text">
                Processing your deletion request...
              </p>
            )}

            <div className="delete-modal-actions">
              <button
                className="delete-modal-button delete-back-btn"
                onClick={() => setShowWarningModal(false)}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                className="delete-modal-button deleted-btn"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFailureModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-content delete-failure-modal">
            <button
              className="delete-modal-close"
              onClick={() => setShowFailureModal(false)}
            >
              ×
            </button>
            <h2>Failed</h2>
            <p>Account deletion failed. Please try again.</p>
            <div className="delete-modal-actions">
              <button
                className="delete-modal-button delete-try-again-btn"
                onClick={handleTryAgain}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {successfulDelete && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-content delete-warning-modal">
            <div className="delete-modal-successful-wrapper">
              <h2 className="delete-modal-successful">Submitted Successfully</h2>
              <p className="delete-modal-kindly">
                Kindly check your email for the concluding part of this deletion
                process.
              </p>
              <button
                className="delete-modal-button finalize-delete"
                onClick={finalizeDelete}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletedAccountPage;
